import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import { TrashIcon } from '@heroicons/react/20/solid';
import { Fragment, FunctionComponent } from 'react';
import { useEnvironment } from '../../hooks/env';

import { ApolloQueryResult } from '@apollo/client/index.js';

import type { Attachment as AttachmentType } from '@pm/graphql';

import {
  Exact,
  GetConversationQuery,
  useDeleteAttachmentMutation,
} from '@pm/graphql';
import { Form, Formik } from 'formik';

import toast from 'react-hot-toast';

import * as Yup from 'yup';

import { Button } from '../Button';
import { StyledField } from '../StyledField';

const schema = Yup.object({
  reason: Yup.string().required('Please provide a reason'),
});

export type AttachmentComponentType = {
  attachment: AttachmentType;
  handleAttachmentClick: React.MouseEventHandler<HTMLAnchorElement>;
  refetch?: (
    variables?: Partial<Exact<{ id: string }>> | undefined,
  ) => Promise<ApolloQueryResult<GetConversationQuery>>;
  removeable?: boolean;
};

export const Attachment: FunctionComponent<AttachmentComponentType> = ({
  attachment,
  handleAttachmentClick,
  refetch,
  removeable = false,
}) => {
  const { apiHost } = useEnvironment();
  const downloadUrl = `${apiHost}${attachment.downloadUrl}`;
  const [deleteAttachment] = useDeleteAttachmentMutation();

  return (
    <li className="col-span-1 flex rounded-md bg-white shadow-xs">
      <a
        href={downloadUrl}
        download
        className="bg-brand-primary-500 flex w-16 shrink-0 items-center justify-center gap-3 rounded-l-md text-sm font-medium leading-5 text-white"
        aria-label="Download"
        onClick={(e) => {
          handleAttachmentClick?.(e);
        }}
      >
        <FontAwesomeIcon icon={faDownload} />
      </a>
      {removeable && (
        <div className="hover:bg-grey-200 relative flex w-11 shrink-0 items-center justify-center gap-3 text-sm font-medium leading-5">
          <Popover as={Fragment}>
            <PopoverButton className="h-full w-full" aria-label="Remove">
              <TrashIcon className="m-auto h-5" />
            </PopoverButton>
            <Transition
              as={Fragment}
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveTo="transform scale-95 opacity-0"
            >
              <PopoverPanel
                style={{ zIndex: '1' }}
                className="border-1 1 text-grey-900 w-48 rounded-md bg-white p-2 shadow-lg"
              >
                {({ close }) => (
                  <Formik
                    initialValues={{ reason: '' }}
                    validationSchema={schema}
                    onSubmit={async ({ reason }) => {
                      await deleteAttachment({
                        variables: {
                          input: {
                            attachmentId: attachment.id,
                            reason,
                          },
                        },
                      }).then(() => {
                        close();
                        toast.success('Attachment deleted');
                        if (refetch) refetch();
                      });
                    }}
                  >
                    {({ values, resetForm }) => (
                      <Form>
                        <div className="text-grey-600 -mb-2 text-sm font-thin">{`Deleting ${attachment.filename}`}</div>
                        <StyledField name="reason" placeholder="Reason" />
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            className="bg-grey-100 text-grey-700 hover:bg-grey-200 focus:border-grey-300 active:bg-grey-200 border border-transparent transition focus:outline-hidden focus:ring-3"
                            disabled={!values.reason}
                            onClick={() => {
                              close();
                              resetForm();
                            }}
                          >
                            Cancel
                          </Button>
                          <Button type="submit" variant="critical">
                            Delete
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                )}
              </PopoverPanel>
            </Transition>
          </Popover>
        </div>
      )}
      <div className="truncate p-2" title={attachment.filename}>
        {attachment.filename}
      </div>
    </li>
  );
};
